import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Chrome } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Eyebrow, EASE } from "../components/Reveal";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const dest = (u) => location.state?.from || (u.role === "admin" ? "/admin" : "/portal");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form);
      navigate(dest(u), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-obsidian text-white grain relative flex items-center justify-center py-24 px-4">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-md" data-testid="auth-card">
        <div className="text-center mb-10">
          <img src="/logo.jpg" alt="Mixed By Empress" className="w-20 h-20 rounded-full object-cover ring-1 ring-[#D4AF37]/50 shadow-2xl mx-auto mb-6" data-testid="auth-logo" />
          <Eyebrow dark>Client Access</Eyebrow>
          <h1 className="font-serif text-4xl mt-4">
            {mode === "login" ? <>Welcome <span className="italic gold-gradient-text">back</span></> : <>Join the <span className="italic gold-gradient-text">studio</span></>}
          </h1>
        </div>

        <div className="bg-[#18171E] border border-white/10 rounded-3xl p-8">
          <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-white/5 mb-8" data-testid="auth-mode-tabs">
            {["login", "register"].map((m) => (
              <button key={m} onClick={() => { setMode(m); setError(""); }} data-testid={`auth-tab-${m}`}
                className={`py-2.5 rounded-full text-sm transition-colors ${mode === m ? "bg-gold text-obsidian font-semibold" : "text-white/60 hover:text-white"}`}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                data-testid="auth-name-input"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm placeholder-white/30 focus:outline-none focus:border-gold" />
            )}
            <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
              data-testid="auth-email-input"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm placeholder-white/30 focus:outline-none focus:border-gold" />
            <input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6}
              data-testid="auth-password-input"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm placeholder-white/30 focus:outline-none focus:border-gold" />
            {mode === "register" && (
              <input type="tel" placeholder="Phone (for text confirmations)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="auth-phone-input"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm placeholder-white/30 focus:outline-none focus:border-gold" />
            )}
            {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="auth-submit-button"
              className="w-full py-4 rounded-full bg-gold text-obsidian font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-50">
              {busy ? "One moment…" : mode === "login" ? "Sign in" : "Create my account"}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/40 font-mono">or</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <button onClick={googleLogin} data-testid="auth-google-button"
            className="w-full py-4 rounded-full border border-white/15 text-sm text-white/80 hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-3">
            <Chrome size={16} /> Continue with Google
          </button>
        </div>

        <p className="text-center text-xs text-white/40 mt-8">
          Just visiting? <Link to="/book" className="text-purple-300 hover:text-gold transition-colors" data-testid="auth-guest-booking-link">Book as a guest</Link> — no account needed.
        </p>
      </motion.div>
    </div>
  );
}
